import { Avatar, Group, Image, Stack, Text } from '@mantine/core'
import { IconPhotoScan } from '@tabler/icons-react'
import { useMemo } from 'react'

import avatars from '@/assets/avatars'
import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
import { EmptyState } from '@/components/EmptyState'
import { TableContentGrid } from '@/components/TableContentGrid'
import { useAllContentCreators } from '@/hooks/useAllContentCreators'
import { useAllEsks } from '@/hooks/useAllEsks'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useTopicsEnabled } from '@/hooks/useTopicsEnabled'
import { getDefaultPskAvatar } from '@/screens/SetupScreen/utils'
import { isHelpModule } from '@/utils/contentModuleTables'

import { useContentModuleScreenData } from './ContentModuleScreen'

export function ModuleSection() {
  const { configStore } = useGlobalState()
  const data = useContentModuleScreenData()
  const { topicsEnabled } = useTopicsEnabled()
  const { allEsks, isReady: isEskDataReady, sidekicksQuery } = useAllEsks()
  const { allContentCreators } = useAllContentCreators()
  const { isMediaMode } = data

  const eskName = useMemo(() => {
    if (data.cmQuery.data?.visibleToMsk) {
      return 'Personal Sidekick'
    }

    const selectEsk = data.cmQuery.data?.selectEsk
    if (selectEsk) {
      // PSK is the internal ID but users see "Personal Sidekick"
      if (selectEsk === 'PSK') {
        return 'Personal Sidekick'
      }

      if (!isEskDataReady) {
        return ''
      }

      const esk = allEsks.find(e => e.eskId === selectEsk)
      return esk?.name ?? `Avatar ${selectEsk}`
    }

    return 'No Sidekick Selected'
  }, [data.cmQuery.data?.selectEsk, data.cmQuery.data?.visibleToMsk, allEsks, isEskDataReady])

  const eskAvatar = useMemo(() => {
    if (data.cmQuery.data?.visibleToMsk) {
      return getDefaultPskAvatar(sidekicksQuery.data)
    }

    const selectEsk = data.cmQuery.data?.selectEsk
    if (!selectEsk) return undefined

    const esk = allEsks.find(e => e.eskId === selectEsk)
    return esk ? avatars[esk.avatarId] : undefined
  }, [data.cmQuery.data?.selectEsk, data.cmQuery.data?.visibleToMsk, allEsks, sidekicksQuery.data])

  // Resolve owner display info. Precedence: API user record > additionalOwners entry > raw owner field.
  const ownerInfo = useMemo(() => {
    const createdBy = data.cmQuery.data?.createdBy
    const additionalOwners = data.cmQuery.data?.additionalOwners ?? []

    if (!createdBy) {
      return {
        primaryName: data.cmQuery.data?.owner || 'Unknown',
        primaryEmail: '',
        extras: additionalOwners,
      }
    }

    const user = allContentCreators.find(u => u.userId === createdBy)
    const ownerEntry = additionalOwners.find(ao => ao.userId === createdBy)

    const primaryName = user?.name || ownerEntry?.name || ''
    const primaryEmail = user?.email || ownerEntry?.email || data.cmQuery.data?.owner || ''

    const extras = additionalOwners.filter(ao => ao.userId !== createdBy)

    return { primaryName, primaryEmail, extras }
  }, [data.cmQuery.data?.createdBy, data.cmQuery.data?.additionalOwners, data.cmQuery.data?.owner, allContentCreators])

  const rows = useMemo(() => {
    const baseRows = [
      { heading: 'Title', content: <Text fw={600}>{data.cmQuery.data?.title}</Text> },
      {
        heading: 'Owner(s)',
        content: (
          <Stack gap={4}>
            <div>
              <Text fw={500}>{ownerInfo.primaryName || ownerInfo.primaryEmail || 'Unknown'}</Text>
              {ownerInfo.primaryName && ownerInfo.primaryEmail && (
                <Text size="sm" c="dimmed">
                  {ownerInfo.primaryEmail}
                </Text>
              )}
            </div>
            {ownerInfo.extras.map(ao => {
              const displayName = ao.name || ao.email || ao.userId
              return (
                <div key={ao.userId}>
                  <Text size="sm" c="dimmed" fw={600}>
                    {displayName}
                  </Text>
                  {ao.name && ao.email && (
                    <Text size="sm" c="dimmed">
                      {ao.email}
                    </Text>
                  )}
                </div>
              )
            })}
          </Stack>
        ),
      },
      {
        heading: 'Sidekick',
        content: (
          <Group align="center">
            <Avatar src={eskAvatar} variant={!!eskAvatar ? 'transparent' : 'light'} radius="sm" size="lg" />
            <Text>{eskName}</Text>
          </Group>
        ),
      },
      {
        heading: 'Module Description',
        content: <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.shortDescription}</Text>,
      },
      ...(topicsEnabled && data.cmQuery.data && !isHelpModule(data.cmQuery.data)
        ? [
            {
              heading: 'Topic',
              content: (
                <Text c={data.cmQuery.data?.categories?.length ? undefined : 'dimmed'}>
                  {data.cmQuery.data?.categories?.length
                    ? data.cmQuery.data.categories.join(', ')
                    : 'No topic assigned'}
                </Text>
              ),
            },
          ]
        : []),
      // Hidden in media mode (BE-generated; not shown in the editor either).
      ...(isMediaMode
        ? []
        : [
            {
              heading: 'Target Audience',
              content: (
                <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.demographic || 'Not specified'}</Text>
              ),
            },
            {
              heading: 'Module Introduction',
              content: (
                <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.introduction || 'Not specified'}</Text>
              ),
            },
            {
              heading: 'Module Image',
              content: data.cmQuery.data?.titleImage ? (
                <Image
                  fit="contain"
                  radius="md"
                  h="auto"
                  mah="300px"
                  w="auto"
                  maw="100%"
                  src={`${configStore.serverBaseUrl}/files/${data.cmQuery.data?.titleImage}`}
                />
              ) : (
                <EmptyState size="sm" Icon={IconPhotoScan} title="No Module Image Uploaded" />
              ),
            },
          ]),
    ]

    return baseRows
  }, [data.cmQuery.data, ownerInfo, eskName, eskAvatar, configStore.serverBaseUrl, topicsEnabled, isMediaMode])

  return (
    <CollapsibleContentSection
      id="module-details"
      title="Module Details"
      subtitle="General module information and user experience settings."
      ContentElement={<TableContentGrid headingFlexWidth="245px" rows={rows} />}
    />
  )
}
